import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Video, Upload, Trash2, Clock, Play, PlusCircle, X, Scissors } from "lucide-react";
import TrimSlider from "./capture/TrimSlider";

const fmtTime = (sec) => {
  const s = Math.max(0, Math.floor(Number(sec) || 0));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
};

/**
 * VideoLibraryCard — shows the video library for a student.
 * role = "coach" (full edit) or "student" (view + own uploads).
 * Coach can upload for any of their students + add timestamped shot notes.
 * Student uploads match footage for their coach to review.
 */
export default function VideoLibraryCard({ studentId, role }) {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showUpload, setShowUpload] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [activeVideo, setActiveVideo] = useState(null);

  const [uForm, setUForm] = useState({ title: "", description: "", file: null });
  // Trim preview state (lives only while the upload form is open).
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewDuration, setPreviewDuration] = useState(0);
  const [trim, setTrim] = useState([0, 0]);
  const [scrubTime, setScrubTime] = useState(null);
  const [trimMessage, setTrimMessage] = useState("");
  const previewVideoRef = useRef(null);

  // Reset preview when a new file is picked or the form is closed.
  const setPickedFile = (file) => {
    if (previewUrl) {
      try { URL.revokeObjectURL(previewUrl); } catch { /* */ }
    }
    setUForm((cur) => ({ ...cur, file }));
    if (file) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    } else {
      setPreviewUrl(null);
      setPreviewDuration(0);
      setTrim([0, 0]);
    }
    setScrubTime(null);
    setTrimMessage("");
  };

  const closeUploadForm = () => {
    setShowUpload(false);
    if (previewUrl) {
      try { URL.revokeObjectURL(previewUrl); } catch { /* */ }
    }
    setPreviewUrl(null);
    setPreviewDuration(0);
    setTrim([0, 0]);
    setScrubTime(null);
    setTrimMessage("");
    setUForm({ title: "", description: "", file: null });
  };

  // Scrub the preview <video> when the trim slider asks for a new time.
  useEffect(() => {
    if (scrubTime == null) return;
    const v = previewVideoRef.current;
    if (!v || !Number.isFinite(v.duration) || v.duration <= 0) return;
    try {
      const t = Math.max(0, Math.min(v.duration - 0.05, scrubTime));
      if (Math.abs((v.currentTime || 0) - t) > 0.03) {
        v.currentTime = t;
        if (v.paused) {
          const pp = v.play();
          if (pp && typeof pp.then === "function") {
            pp.then(() => { try { v.pause(); } catch { /* */ } })
              .catch(() => { /* AbortError — fine */ });
          }
        }
      }
    } catch { /* */ }
  }, [scrubTime]);

  // Pick a MediaRecorder mime type the browser supports. Returns "" if none
  // — we'll fall back to the original file in that case.
  const pickRecorderMime = () => {
    const candidates = [
      "video/mp4;codecs=avc1",
      "video/mp4",
      "video/webm;codecs=h264",
      "video/webm;codecs=vp9",
      "video/webm;codecs=vp8",
      "video/webm",
    ];
    for (const m of candidates) {
      try {
        if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(m)) {
          return m;
        }
      } catch { /* */ }
    }
    return "";
  };

  // Record the trim window [start, end] from the preview video into a Blob
  // via videoElement.captureStream() + MediaRecorder. Real-time — a 30s trim
  // takes ~30s. Returns the recorded Blob or throws.
  const recordTrimWindow = async (start, end) => {
    const v = previewVideoRef.current;
    if (!v) throw new Error("Preview video not ready");
    if (typeof v.captureStream !== "function") {
      throw new Error("This browser doesn't support trim — upload the full clip instead");
    }
    const mime = pickRecorderMime();
    const stream = v.captureStream();
    const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
    const chunks = [];
    rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunks.push(e.data); };
    const stopped = new Promise((resolve) => { rec.onstop = () => resolve(); });
    // Seek to start, wait for the seek to land, then start recording + play
    v.pause();
    v.currentTime = Math.max(0, start);
    await new Promise((r) => {
      const onSeeked = () => { v.removeEventListener("seeked", onSeeked); r(); };
      v.addEventListener("seeked", onSeeked);
      setTimeout(r, 800);
    });
    rec.start(500);
    await v.play();
    // Watch playback and stop at trim end (or natural end)
    await new Promise((resolve) => {
      const onTime = () => {
        if (v.currentTime >= end) {
          v.removeEventListener("timeupdate", onTime);
          v.pause();
          resolve();
        }
      };
      v.addEventListener("timeupdate", onTime);
      v.addEventListener("ended", () => {
        v.removeEventListener("timeupdate", onTime);
        resolve();
      }, { once: true });
    });
    try { rec.stop(); } catch { /* */ }
    await stopped;
    return new Blob(chunks, { type: mime || "video/webm" });
  };

  const load = async () => {
    try {
      const { data } = await api.get(`/videos/student/${studentId}`);
      setVideos(data || []);
    } catch (_) {}
    setLoading(false);
  };
  useEffect(() => { if (studentId) load(); }, [studentId]);

  const upload = async (e) => {
    e.preventDefault();
    if (!uForm.file) { toast.error("Pick a video file"); return; }
    if (!uForm.title.trim()) { toast.error("Add a title"); return; }
    setUploading(true);
    setProgress(0);
    try {
      // Decide whether the user actually wants the trim window or the full
      // file. Trim is "active" only if the window is meaningfully shorter
      // than the source.
      const dur = previewDuration;
      const isTrimmed = dur > 0 && (trim[0] > 0.5 || trim[1] < dur - 0.5) && trim[1] > trim[0] + 0.3;

      let blob = uForm.file;
      let filename = uForm.file.name || "clip.mp4";
      if (isTrimmed) {
        try {
          setTrimMessage(`Recording trimmed window (${Math.round(trim[1] - trim[0])}s)…`);
          blob = await recordTrimWindow(trim[0], trim[1]);
          const ext = (blob.type || "").includes("mp4") ? "mp4" : "webm";
          // Try to preserve the original name with a -trimmed suffix
          const baseName = (uForm.file.name || "clip").replace(/\.[^.]+$/, "");
          filename = `${baseName}-trimmed.${ext}`;
          setTrimMessage("");
        } catch (err) {
          setTrimMessage("");
          toast.error(err?.message || "Could not record trimmed clip — falling back to full file.");
          blob = uForm.file;
          filename = uForm.file.name || "clip.mp4";
        }
      }

      const fd = new FormData();
      fd.append("student_id", studentId);
      fd.append("title", uForm.title.trim());
      fd.append("description", uForm.description.trim());
      // FormData expects a File or Blob — wrap Blob with a filename so the
      // backend's mime sniff + extension derivation still works.
      const fileLike = blob instanceof File ? blob : new File([blob], filename, { type: blob.type || "video/webm" });
      fd.append("file", fileLike);
      await api.post("/videos", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded * 100) / evt.total));
        },
      });
      toast.success(isTrimmed ? "Trimmed clip uploaded" : "Video uploaded");
      closeUploadForm();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    }
    setUploading(false);
    setProgress(0);
  };

  const remove = async (v) => {
    if (!window.confirm(`Delete "${v.title}"?`)) return;
    try {
      await api.delete(`/videos/${v.id}`);
      toast.success("Deleted");
      if (activeId === v.id) { setActiveId(null); setActiveVideo(null); }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete");
    }
  };

  const open = async (v) => {
    setActiveId(v.id);
    try {
      const { data } = await api.get(`/videos/${v.id}`);
      setActiveVideo(data);
    } catch (_) { toast.error("Could not load video"); }
  };

  return (
    <div className="card-surface p-8" data-testid="video-library-card">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold flex items-center gap-2">
            <Video size={12} /> Video Library
          </div>
          <h3 className="font-display text-3xl font-black uppercase tracking-tighter mt-2">
            {role === "coach" ? "Review footage & add shot notes" : "Your coaching videos"}
          </h3>
          <p className="text-xs text-[#A3A3A3] mt-2 max-w-xl">
            {role === "coach"
              ? "Upload drill clips or review a student's match footage. Scrub to any moment and leave a timestamped note — the student will see it on playback."
              : "Videos your coach uploaded, plus any match clips you've sent in. Tap a clip to watch; timestamps below jump to key moments."}
          </p>
        </div>
        <button
          onClick={() => setShowUpload(true)}
          className="btn-primary px-5 py-2.5 text-xs inline-flex items-center gap-2"
          data-testid="video-upload-open"
        >
          <Upload size={14} /> Upload video
        </button>
      </div>

      {showUpload && (
        <form onSubmit={upload} className="mt-5 border border-white/10 p-5 space-y-3" data-testid="video-upload-form">
          <div className="flex justify-between items-start">
            <div className="text-[10px] uppercase tracking-widest text-[#38BDF8] font-bold">New clip</div>
            <button type="button" onClick={closeUploadForm} className="text-[#A3A3A3] hover:text-white" data-testid="video-upload-close"><X size={14} /></button>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-bold">Title</label>
            <input
              type="text" value={uForm.title} onChange={(e) => setUForm({ ...uForm, title: e.target.value })}
              placeholder="Forehand drill — session 1"
              className="w-full bg-transparent border-b border-white/20 focus:border-[#38BDF8] outline-none py-2 text-sm"
              data-testid="video-title-input"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-bold">Notes (optional)</label>
            <input
              type="text" value={uForm.description} onChange={(e) => setUForm({ ...uForm, description: e.target.value })}
              placeholder="Focus on follow-through"
              className="w-full bg-transparent border-b border-white/20 focus:border-[#38BDF8] outline-none py-2 text-sm"
              data-testid="video-description-input"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-bold">File (mp4 / mov / webm)</label>
            <input
              type="file"
              accept="video/mp4,video/quicktime,video/webm,video/x-matroska,video/ogg"
              onChange={(e) => setPickedFile(e.target.files?.[0] || null)}
              className="w-full text-sm mt-1 file:mr-3 file:py-2 file:px-4 file:border file:border-white/10 file:bg-transparent file:text-white file:text-xs file:uppercase file:tracking-widest"
              data-testid="video-file-input"
            />
            {uForm.file && (
              <div className="text-[11px] text-[#A3A3A3] mt-1">
                {uForm.file.name} · {(uForm.file.size / (1024*1024)).toFixed(1)} MB
              </div>
            )}
          </div>

          {/* Trim preview — appears when a file is picked. Coach can scrub
              start/end and only that window will be uploaded. Real-time
              recording, so the wait equals the trim duration (a 30s trim
              of a 60min clip takes 30s, not 60min). */}
          {previewUrl && (
            <div className="space-y-2 border-t border-white/10 pt-3" data-testid="video-trim-section">
              <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-bold flex items-center gap-1.5">
                <Scissors size={11} /> Trim before upload (optional)
              </div>
              <video
                ref={previewVideoRef}
                src={previewUrl}
                controls
                playsInline
                muted
                preload="metadata"
                onLoadedMetadata={(e) => {
                  const d = e.currentTarget.duration;
                  if (Number.isFinite(d) && d > 0) {
                    setPreviewDuration(d);
                    setTrim([0, d]);
                  }
                }}
                className="w-full max-h-[50vh] bg-black"
                data-testid="video-trim-preview"
              />
              {previewDuration > 0 && (
                <TrimSlider
                  duration={previewDuration}
                  value={trim}
                  onChange={setTrim}
                  onScrubTo={(t) => setScrubTime(t)}
                />
              )}
              <p className="text-[11px] text-[#A3A3A3] leading-relaxed">
                Leave the handles at the ends to upload the full clip. Drag them inward to record just the chosen window — only the trimmed bytes get sent.
              </p>
              {trimMessage && (
                <div className="text-[11px] text-[#38BDF8] uppercase tracking-widest" data-testid="video-trim-status">
                  {trimMessage}
                </div>
              )}
            </div>
          )}

          {uploading && (
            <div className="space-y-1" data-testid="video-upload-progress">
              <div className="h-1 bg-white/10 overflow-hidden">
                <div className="h-full bg-[#38BDF8] transition-all" style={{ width: `${progress}%` }} />
              </div>
              <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">Uploading… {progress}%</div>
            </div>
          )}
          <button disabled={uploading} type="submit" className="btn-primary px-5 py-2.5 text-xs inline-flex items-center gap-2" data-testid="video-upload-submit">
            <Upload size={14} /> {uploading ? "Uploading…" : "Upload"}
          </button>
        </form>
      )}

      <div className="mt-6 space-y-3">
        {loading && <div className="text-sm text-[#A3A3A3]">Loading…</div>}
        {!loading && videos.length === 0 && (
          <div className="border border-dashed border-white/10 p-6 text-sm text-[#A3A3A3]">
            No clips yet. Upload drill footage or a match clip to get started.
          </div>
        )}
        {videos.map((v) => (
          <VideoRow
            key={v.id}
            video={v}
            active={activeId === v.id}
            activeVideo={activeVideo}
            role={role}
            onOpen={() => open(v)}
            onClose={() => { setActiveId(null); setActiveVideo(null); }}
            onDelete={() => remove(v)}
            onChanged={(updated) => { setActiveVideo(updated); load(); }}
          />
        ))}
      </div>
    </div>
  );
}

function VideoRow({ video, active, activeVideo, role, onOpen, onClose, onDelete, onChanged }) {
  const [noteText, setNoteText] = useState("");
  const [saving, setSaving] = useState(false);
  const playerRef = useRef(null);

  const v = active && activeVideo ? activeVideo : video;
  const streamUrl = `${process.env.REACT_APP_BACKEND_URL}/api/videos/${v.id}/stream`;

  const addNote = async () => {
    if (!noteText.trim()) { toast.error("Write a note first"); return; }
    setSaving(true);
    try {
      const ts = playerRef.current ? playerRef.current.currentTime : 0;
      const { data: note } = await api.post(`/videos/${v.id}/notes`, {
        text: noteText.trim(), timestamp_sec: ts,
      });
      const updated = { ...v, notes: [...(v.notes || []), note] };
      onChanged(updated);
      setNoteText("");
      toast.success(`Note added @${fmtTime(ts)}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save note");
    }
    setSaving(false);
  };

  const removeNote = async (noteId) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await api.delete(`/videos/${v.id}/notes/${noteId}`);
      const updated = { ...v, notes: (v.notes || []).filter((n) => n.id !== noteId) };
      onChanged(updated);
    } catch (_) { toast.error("Could not delete"); }
  };

  const seekTo = (sec) => {
    if (playerRef.current) {
      playerRef.current.currentTime = Math.max(0, Number(sec) || 0);
      playerRef.current.play().catch(() => {});
    }
  };

  return (
    <div className="border border-white/10" data-testid={`video-row-${v.id}`}>
      <div className="p-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="font-bold truncate">{v.title}</div>
          <div className="text-[11px] text-[#A3A3A3] mt-0.5">
            {v.uploader_role === "coach" ? "Coach" : v.uploader_name || "Student"} · {new Date(v.created_at).toLocaleDateString()}
            {v.notes?.length ? ` · ${v.notes.length} note${v.notes.length !== 1 ? "s" : ""}` : ""}
          </div>
          {v.description && <div className="text-xs text-white/80 mt-1">{v.description}</div>}
        </div>
        <div className="flex gap-2">
          {active ? (
            <button onClick={onClose} className="btn-secondary px-4 py-2 text-xs inline-flex items-center gap-1" data-testid={`video-close-${v.id}`}>
              <X size={12} /> Close
            </button>
          ) : (
            <button onClick={onOpen} className="btn-primary px-4 py-2 text-xs inline-flex items-center gap-1" data-testid={`video-open-${v.id}`}>
              <Play size={12} /> Watch
            </button>
          )}
          {(role === "coach" || v.uploader_role === "student") && (
            <button onClick={onDelete} className="btn-secondary px-3 py-2 text-xs inline-flex items-center" data-testid={`video-delete-${v.id}`}>
              <Trash2 size={12} />
            </button>
          )}
        </div>
      </div>

      {active && activeVideo && (
        <div className="border-t border-white/10 p-4 space-y-4 bg-black/30" data-testid={`video-player-${v.id}`}>
          <video
            ref={playerRef}
            controls
            preload="metadata"
            src={streamUrl}
            className="w-full max-h-[70vh] bg-black"
            crossOrigin="use-credentials"
            data-testid={`video-element-${v.id}`}
          />

          {role === "coach" && (
            <div className="border border-white/10 p-4">
              <div className="text-[10px] uppercase tracking-widest text-[#38BDF8] font-bold flex items-center gap-2">
                <Clock size={12} /> Note at current time
              </div>
              <div className="mt-2 flex gap-2 items-start">
                <input
                  type="text" value={noteText} onChange={(e) => setNoteText(e.target.value)}
                  placeholder="e.g. Great paddle angle — hit this consistently"
                  className="flex-1 bg-transparent border-b border-white/20 focus:border-[#38BDF8] outline-none py-2 text-sm"
                  data-testid={`video-note-text-${v.id}`}
                />
                <button onClick={addNote} disabled={saving} className="btn-primary px-4 py-2 text-xs inline-flex items-center gap-1 whitespace-nowrap" data-testid={`video-note-save-${v.id}`}>
                  <PlusCircle size={12} /> Add at {fmtTime(playerRef.current?.currentTime || 0)}
                </button>
              </div>
            </div>
          )}

          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-bold mb-2">
              Shot notes ({(v.notes || []).length})
            </div>
            {(v.notes || []).length === 0 ? (
              <div className="text-xs text-[#A3A3A3]">No notes yet.</div>
            ) : (
              <div className="space-y-2" data-testid={`video-notes-list-${v.id}`}>
                {[...(v.notes || [])].sort((a, b) => a.timestamp_sec - b.timestamp_sec).map((n) => (
                  <div key={n.id} className="flex items-start gap-3 border-l-2 border-[#38BDF8] pl-3 py-1" data-testid={`video-note-${n.id}`}>
                    <button
                      onClick={() => seekTo(n.timestamp_sec)}
                      className="text-[#38BDF8] font-mono text-xs hover:underline whitespace-nowrap"
                      data-testid={`video-note-seek-${n.id}`}
                    >
                      @{fmtTime(n.timestamp_sec)}
                    </button>
                    <div className="flex-1 text-sm">{n.text}</div>
                    {role === "coach" && (
                      <button onClick={() => removeNote(n.id)} className="text-[#A3A3A3] hover:text-red-400" data-testid={`video-note-delete-${n.id}`}>
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
